/** Personal lesson card using learner UI primitives — play, delete, move-to-folder. */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Trash2 } from 'lucide-react';
import { Button as AriaButton } from 'react-aria-components';
import { toast } from 'sonner';
import { Surface, Dialog, Badge, Button } from '@/interfaces/learner/ui';
import { useDeletePersonalLesson } from '@/shared/api/hooks';
import { useSessionGuard } from '@/shared/hooks/useSessionGuard';
import { formatRelativeTime } from '@/shared/utils/formatRelativeTime';
import { MoveToFolderMenu } from './MoveToFolderMenu';
import { slugify } from '../utils';
import type { PersonalLesson } from '@formency/shared';

interface LessonCardProps {
  lesson: PersonalLesson;
  /** Accent bar color from the folder, if any */
  accentColor?: string;
}

export function LessonCard({ lesson, accentColor }: LessonCardProps) {
  const navigate = useNavigate();
  const deleteLesson = useDeletePersonalLesson();
  const { guardSession } = useSessionGuard();
  const [showDelete, setShowDelete] = useState(false);

  const totalSteps = lesson.learnStepCount;

  const folderSlug = lesson.folderName ? slugify(lesson.folderName) : 'unfiled';

  const handlePlay = () => {
    guardSession({
      lessonId: lesson.lessonId,
      onProceed: () => navigate(`/lessons/${folderSlug}/${lesson.lessonId}`),
    });
  };

  const handleDelete = () => {
    deleteLesson.mutate(lesson.id, {
      onSuccess: () => setShowDelete(false),
      onError: (err) => toast.error(err.message || 'Failed to delete lesson'),
    });
  };

  return (
    <>
      <Surface accent={accentColor}>
        <div style={{ padding: '14px 16px 14px' }}>
          {/* Folder badge */}
          {lesson.folderName && (
            <div style={{ marginBottom: 6 }}>
              <Badge variant="default" style={{ fontSize: 11 }}>
                {lesson.folderName}
              </Badge>
            </div>
          )}

          {/* Title */}
          <h3
            className="text-list-title"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              margin: 0,
            }}
          >
            {lesson.title}
          </h3>

          {/* Bottom row */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: 12,
            }}
          >
            {/* Meta: step count + time */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Badge variant="default">
                {totalSteps} {totalSteps === 1 ? 'step' : 'steps'}
              </Badge>
              <span
                style={{
                  color: 'var(--color-text-muted)',
                }}
              >
                {formatRelativeTime(lesson.createdAt)}
              </span>
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Move to folder */}
              <MoveToFolderMenu lessonId={lesson.lessonId} currentFolderId={lesson.folderId} />

              {/* Play */}
              <AriaButton
                onPress={handlePlay}
                aria-label="Play lesson"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 28,
                  height: 28,
                  borderRadius: 'var(--control-radius-sm)',
                  background: 'var(--color-accent-subtle)',
                  border: 'none',
                  color: 'var(--color-accent-text)',
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                }}
                className="hover:bg-[var(--color-accent)] hover:text-[var(--color-text-on-accent)]"
              >
                <Play size={13} strokeWidth={2} />
              </AriaButton>

              {/* Delete */}
              <AriaButton
                onPress={() => setShowDelete(true)}
                aria-label="Delete lesson"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 28,
                  height: 28,
                  borderRadius: 'var(--control-radius-sm)',
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--color-text-muted)',
                  cursor: 'pointer',
                  transition: 'background 0.15s, color 0.15s',
                }}
                className="hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-error)]"
              >
                <Trash2 size={13} strokeWidth={1.75} />
              </AriaButton>
            </div>
          </div>
        </div>
      </Surface>

      {/* Delete confirmation dialog */}
      <Dialog
        isOpen={showDelete}
        onClose={() => setShowDelete(false)}
        title="Delete lesson?"
        size="sm"
      >
        <p
          className="text-body"
          style={{
            margin: 0,
          }}
        >
          This will permanently delete this lesson and all its content.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <Button variant="ghost" size="sm" onPress={() => setShowDelete(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            isDisabled={deleteLesson.isPending}
            onPress={handleDelete}
          >
            Delete
          </Button>
        </div>
      </Dialog>
    </>
  );
}
